import { atLocalHour, dateInputIn } from '../time/index.js';

/**
 * WHEN A LEAGUE'S EMAILS GO — Courtney's Communication Guidance, as she answered it on 2026-10-12.
 *
 *   Kickoff            9 AM, four calendar days before session one.
 *   Reminder           9 AM, four calendar days before every later session (the last one too).
 *   Celebration        once that session's scores are CLOSED: 9 AM the morning after the session,
 *                      or straight away if they close later. The last session's is the final one.
 *
 * Every time is in the host city's zone. Pure, and computed rather than stored: moving a session
 * moves its unsent emails because there is nothing to move — the plan is read from the sessions.
 */
export const EMAIL_HOUR = 9;
export const DAYS_BEFORE = 4;

/** 9 AM in the league's city, `days` calendar days from the session's own date there. */
function nineOn(startsAt: Date, zone: string, days: number): Date {
  const [year, month, date] = dateInputIn(startsAt, zone).split('-').map(Number);
  /* A calendar step from the DATE, never 24-hour steps: four days can cross a clock change. */
  const day = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, (date ?? 1) + days));
  return atLocalHour({ year: day.getUTCFullYear(), month: day.getUTCMonth() + 1, date: day.getUTCDate() }, zone, EMAIL_HOUR);
}

/** The kickoff's and a reminder's time — and a substitute's confirmation, which rides with it. */
export function beforeSession(startsAt: Date, zone: string): Date {
  return nineOn(startsAt, zone, -DAYS_BEFORE);
}

/** The morning after the session, which is when a celebration is aimed. */
export function morningAfter(startsAt: Date, zone: string): Date {
  return nineOn(startsAt, zone, 1);
}

export interface PlannedSession {
  id: string;
  sessionNumber: number;
  startsAt: Date;
  status: string;
  scoresClosedAt: Date | null;
}

export type PlannedKind = 'kickoff' | 'reminder' | 'results' | 'final_results';

export interface PlannedEmail {
  kind: PlannedKind;
  sessionId: string;
  sessionNumber: number;
  /** When it goes. Null while it is waiting for something — a celebration, for closed scores. */
  dueAt: Date | null;
  /** After this it is too late to be worth sending — a reminder for a session that has started. */
  expiresAt: Date | null;
}

/**
 * Every email a league's schedule holds, in order. A voided session has none. `lastSession` is the
 * league's Format length: the last session's celebration is the final celebration, and it replaces
 * the ordinary one rather than following it.
 */
/** How long after it falls due a results email may still go: three days. */
export const CELEBRATION_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

export function planLeagueEmails(sessions: PlannedSession[], zone: string, lastSession: number): PlannedEmail[] {
  const plan: PlannedEmail[] = [];
  for (const session of [...sessions].sort((a, b) => a.sessionNumber - b.sessionNumber)) {
    if (session.status === 'voided') continue;
    const base = { sessionId: session.id, sessionNumber: session.sessionNumber };
    plan.push({
      ...base,
      kind: session.sessionNumber === 1 ? 'kickoff' : 'reminder',
      dueAt: beforeSession(session.startsAt, zone),
      expiresAt: session.startsAt,
    });
    const aimed = morningAfter(session.startsAt, zone);
    const celebrationDue = session.scoresClosedAt ? new Date(Math.max(aimed.getTime(), session.scoresClosedAt.getTime())) : null;
    plan.push({
      ...base,
      kind: session.sessionNumber === lastSession ? 'final_results' : 'results',
      dueAt: celebrationDue,
      /*
        A CELEBRATION IS NEWS FOR A FEW DAYS, NOT FOREVER (2026-10-18). With no expiry, a league whose
        players were invited in week three sent weeks one and two's results to everybody at once the
        next quarter-hour. Due already waits for the scores to close, so closing them late still
        sends promptly; it is only an email that is days overdue that no longer goes.
      */
      expiresAt: celebrationDue ? new Date(celebrationDue.getTime() + CELEBRATION_WINDOW_MS) : null,
    });
  }
  return plan;
}

/**
 * WHAT IS DUE NOW. `from` is when automatic sending began for this league — set for leagues that
 * existed before it did (owner, 2026-10-12: "from their next upcoming email onward"), so nothing
 * that fell due earlier is sent the day this ships. Null for every league made since.
 */
export function dueEmails(plan: PlannedEmail[], now: Date, from: Date | null): PlannedEmail[] {
  return plan.filter(
    (email) =>
      email.dueAt !== null &&
      email.dueAt.getTime() <= now.getTime() &&
      (email.expiresAt === null || now.getTime() < email.expiresAt.getTime()) &&
      (from === null || email.dueAt.getTime() >= from.getTime()),
  );
}

/**
 * EACH KIND OF EMAIL CAN BE TURNED OFF, league by league, in Settings (Courtney, 2026-10-12). One
 * switch covers both celebrations — the last session's replaces the ordinary one, so they are one
 * thing to a director.
 */
export const EMAIL_SWITCHES = [
  { key: 'welcome', label: 'Registration welcome' },
  { key: 'kickoff', label: 'Kickoff' },
  { key: 'reminder', label: 'Session reminders' },
  { key: 'celebration', label: 'Celebrations' },
  { key: 'absence_confirmation', label: 'Absence confirmations' },
  { key: 'substitute_confirmation', label: 'Substitute confirmations' },
] as const;

export type EmailSwitch = (typeof EMAIL_SWITCHES)[number]['key'];

/** The switch an email kind answers to. */
export function emailSwitchFor(kind: string): EmailSwitch | null {
  if (kind === 'results' || kind === 'final_results') return 'celebration';
  return EMAIL_SWITCHES.some((s) => s.key === kind) ? (kind as EmailSwitch) : null;
}

/** Whether this league sends that kind of email. Off only when the director turned it off. */
export function emailOn(emailsOff: readonly string[] | null | undefined, kind: string): boolean {
  const key = emailSwitchFor(kind);
  return key === null || !(emailsOff ?? []).includes(key);
}
